import api from '@/api/axios';
import store from '@/stores/store';
import { Patient } from '@/stores/store.types';
import { logError } from '@/utils/logHandling';
import { LicenseStatus } from './user';

export const serverSyncPatientSettings = async () => {
  return api
    .get(`/api/patients/activeset/V2/sync?api_version=v2`)
    .then((res) => {
      return res;
    })
    .catch((error) => {
      logError('Could not sync patient settings', error);
      throw error;
    });
};

interface serverSyncPatientActiveSetsPayload {
  setsToFetch: {
    sentencesets: string[];
    wordsets: string[];
    textsets: string[];
  };
}

export const serverSyncPatientActiveSets = async (
  payload: serverSyncPatientActiveSetsPayload,
) => {
  return api
    .post(`/api/patients/activeset/V2?api_version=v4`, payload.setsToFetch)
    .then((res) => {
      return res;
    })
    .catch((error) => {
      return Promise.reject(error);
    });
};

export const addPatient = async (payload: Partial<Patient>) => {
  return api
    .post(`/api/patients`, payload)
    .then((res) => {
      return res;
    })
    .catch((error) => {
      return Promise.reject(error);
    });
};

export const fetchPatient = async (patientId: string) => {
  return api
    .get(`/api/patients/${patientId}?api_version=v2`)
    .then((res) => {
      store.getActions().setPatientSettings(res.data);
      return res.data;
    })
    .catch((error) => {
      return Promise.reject(error);
    });
};

export const updatePatientSettings = async (
  payload: Partial<Patient>,
  patientId: string,
) => {
  return api
    .put(`/api/patients/${patientId}?api_version=v2`, payload)
    .then((res) => {
      if (res && res.data && res.data.code && res.data.code.length > 0) {
        return true;
      } else {
        return false;
      }
    })
    .catch((error) => {
      return Promise.reject(error);
    });
};

export const updatePatient = async (
  payload: Partial<Patient>,
  patientId: string,
) => {
  return api
    .put(`/api/patients/${patientId}?api_version=v2`, payload)
    .then((res) => {
      return res;
    })
    .catch((error) => {
      return Promise.reject(error);
    });
};

export const deletePatient = async (patientId: string) => {
  return api
    .delete(`/api/patients/${patientId}`)
    .then((res) => {
      return res.data;
    })
    .catch((error) => {
      return Promise.reject(error);
    });
};

export interface PatientLicenseStatus extends LicenseStatus {
  snoozedAt: string | null;
  isDismissed: boolean;
}

export const getPatientLicenseStatus = async (
  patientId: string,
): Promise<PatientLicenseStatus | null> => {
  try {
    const res = await api.get(`/api/patients/${patientId}/license-status`);
    return res.data.license;
  } catch (error) {
    logError('Could not get license status', error);
    return null;
  }
};

export const getAllPatientsLicenseStatus = async (): Promise<
  { patientId: string; license: PatientLicenseStatus | null }[] | null
> => {
  try {
    const res = await api.get(`/api/patients/license-status`);
    return res.data;
  } catch (error) {
    logError('Could not get license status', error);
    return null;
  }
};

export const dismissLicenseWarning = async (patientId: string) => {
  try {
    await api.patch(
      `/api/patients/${patientId}/license-status/dismiss-reminder`,
    );
  } catch (error) {
    logError('Could not dismiss license warning', error);
    throw error;
  }
};

export const snoozeLicenseWarning = async (patientId: string) => {
  try {
    await api.patch(
      `/api/patients/${patientId}/license-status/snooze-reminder`,
    );
  } catch (error) {
    logError('Could not snooze license warning', error);
    throw error;
  }
};
